import {
  Box,
  Grid,
  Hidden,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import {
  BooleanRadioGroup,
  colors,
  DefaultCard,
  FormHookResult,
  FormValueTypes,
  Modal,
  TextField,
} from 'design-system';
import { useState } from 'react';
// Models
import { CarYearConfig } from 'data-layer-cr/dist/models';
import PassengerItem from 'src/constants/PassengerItem';
import CatalogItem from 'src/models/helperTypes/CatalogItem';
// Functions
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { HelpCircle } from 'react-feather';
import { useStylesHelpModal } from 'src/components/screens/Quotations/components/IndividualDeductibles/DeductibleHelpModal';
import isNewCarForYear from 'src/functions/isNewCarForYear';
import mapToCatalogItem from 'src/functions/mapToCatalogItem';
import { REQUEST_LICENSEPLATE } from 'src/store/quotation/requestIdentifiers';
import { shouldShowCarStateInput } from '../helperFunctions';
// Component
import { Loading, NetworkIndicator } from '../../../../General';
import { MoneyField } from '../../../../Inputs';
import { makeHasInvoiceField } from '../../ClientForm/functions';
import makeFormStyles from '../../styles/makeFormStyles';
import BrandSelector from '../BrandSelector';
import MessageWarning from './MessageWarning';
import PurchaseInvoiceModal from './PurchaseInvoiceModal';
import makeSearchableDropDown from './makeSearchableDropDown';

const passengerList = mapToCatalogItem(PassengerItem);

const moneysList = mapToCatalogItem([
  { id: 1, code: 'CRC', name: 'Colones' },
  { id: 2, code: 'USD', name: 'Dólares' },
]);

type Props = {
  getInputProps: FormHookResult;
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
  setGroupID: (value: string | null | undefined) => void;
  onChangeModel: (val: CatalogItem) => void;
  modelsList: CatalogItem[];
  requestingModels: boolean;
  isCollectiveProfile: boolean;
  groupID?: string | null;
  carYearConfig?: CarYearConfig;
  disabled: boolean;
};

const CarDataSection = ({
  getInputProps,
  updateMultipleInputs,
  setGroupID,
  onChangeModel,
  requestingModels,
  isCollectiveProfile,
  modelsList,
  groupID,
  carYearConfig,
  disabled,
}: Props): JSX.Element => {
  const classes = makeFormStyles();
  const requestLicensePlate = useNetworkObserver(REQUEST_LICENSEPLATE);
  const [openModal, setOpenModal] = useState(false);
  const isNewCar = isNewCarForYear(getInputProps('carYear').value as number);
  const noLicensePlateSelected = getInputProps('noLicensePlate')
    .value as boolean;

  const classesHelpModal = useStylesHelpModal();
  const theme = useTheme();
  const smQuery = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);

  const handleCloseModal = () => setOpen(false);

  const disabledModel =
    !getInputProps('carBrand').value && !getInputProps('carModel').value;

  const moneyValue = moneysList.find(
    (moneyItem: CatalogItem) =>
      moneyItem.Value === (getInputProps('typeOfMoney').value as string),
  )?.Text as string;

  return (
    <Grid item xs={12} sm={8} md={12}>
      <Modal
        data-testid="zero-km-help-modal"
        title={
          <Typography
            style={{
              color: smQuery ? '#0D2E68' : colors.white,
              paddingInline: smQuery ? '3rem' : '0',
            }}
            align="center"
          >
            Auto 0 kilómetros
          </Typography>
        }
        open={open}
        maxWidth="sm"
        onCloseRequested={handleCloseModal}
      >
        <Grid
          container
          item
          justifyContent="center"
          className={
            smQuery
              ? classesHelpModal.rootContainer
              : classesHelpModal.rootContainerLarge
          }
          style={{ paddingBottom: '1rem' }}
        >
          <Grid item container justifyContent="center" data-testid="first-page">
            <Grid item>
              <img
                src="/svg/vehicle_zero_km.svg"
                alt="vehicle_zero_km"
                style={{ width: '270px' }}
              />
            </Grid>

            <Grid
              item
              container
              spacing={1}
              justifyContent="center"
              style={{ maxWidth: '400px', marginTop: '20px' }}
            >
              <Grid item xs={12} container>
                <Typography
                  align="center"
                  className={classesHelpModal.textTitle}
                  style={{ fontSize: 15, fontWeight: 400 }}
                >
                  Este es un vehículo del año, de agencia y 0 kilómetros, si su
                  aseguramiento se está realizando con factura, por favor valide
                  que la misma no tenga más de 5 días naturales de emitida.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
      {requestLicensePlate === 'requesting' ? (
        <Loading />
      ) : (
        <DefaultCard>
          <div className={classes.formCardContent}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <BrandSelector
                  showBrandIcons={false}
                  updateMultipleInputs={updateMultipleInputs}
                  setGroupID={setGroupID}
                  getInputProps={getInputProps}
                  carBrandFieldName="carBrand"
                  carModelFiledName="carModel"
                  disabled={
                    !noLicensePlateSelected &&
                    disabled &&
                    !getInputProps('carModel').value
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {!requestingModels &&
                  makeSearchableDropDown(
                    'sales-web-dropdown-carmodel',
                    'Modelo',
                    'carModel',
                    modelsList,
                    onChangeModel,
                    getInputProps,
                    disabledModel,
                  )}
                <NetworkIndicator
                  data-testid="car-model-loading"
                  variant="horizontal"
                  requesting={requestingModels}
                  message="Obteniendo modelos de auto..."
                />
              </Grid>
              <Hidden mdUp>
                <MessageWarning groupID={groupID} />
              </Hidden>
              <Hidden smDown>
                <MessageWarning groupID={groupID} />
              </Hidden>
              <Grid item xs={12} sm={6}>
                <TextField
                  inputId="sales-web-input-caryear"
                  inputLabel="Año"
                  inputName={getInputProps('carYear').name}
                  defaultValue={getInputProps('carYear').value as string}
                  inputError={getInputProps('carYear').error}
                  inputErrorMessage={getInputProps('carYear').errorText}
                  onChange={(newVal: string): void => {
                    updateMultipleInputs({
                      carYear: newVal,
                      isNewCar: undefined,
                    });
                  }}
                  isDisabled={
                    !noLicensePlateSelected &&
                    disabled &&
                    !getInputProps('carModel').value
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} className={classes.inputRow}>
                {isNewCar && shouldShowCarStateInput(carYearConfig) && (
                  <Box display="flex" sx={{ width: '100%' }}>
                    <Box>
                      <BooleanRadioGroup
                        inputId="sales-web-radio-newcar"
                        inputName="isNewCar"
                        inputLabel="¿Es tu auto 0 kilómetros?"
                        defaultValue={
                          getInputProps('isNewCar').value as boolean
                        }
                        onChange={getInputProps('isNewCar').onChange}
                        inputError={getInputProps('isNewCar').error}
                        inputErrorMessage={getInputProps('isNewCar').errorText}
                      />
                    </Box>
                    <Box
                      data-testid="helper-button"
                      style={{ width: '25px' }}
                      onClick={() => setOpen(true)}
                    >
                      <IconButton>
                        <HelpCircle color={colors.bright_turquoise} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Grid>
              {isCollectiveProfile && (
                <Grid item xs={12} sm={12} className={clsx(classes.inputRow)}>
                  {makeSearchableDropDown(
                    'sales-web-dropdown-passengersQty',
                    'Cantidad de pasajeros',
                    'passengersQty',
                    passengerList,
                    (val: CatalogItem): void =>
                      getInputProps('passengersQty').onChange(val.Value),
                    getInputProps,
                    !noLicensePlateSelected,
                  )}
                </Grid>
              )}
              <Grid item xs={5} sm={6}>
                <TextField
                  inputId="sales-web-dropdown-moneytype"
                  inputLabel="Tipo de moneda"
                  inputName="typeOfMoney"
                  defaultValue={moneyValue}
                  inputError={getInputProps('typeOfMoney').error}
                  inputErrorMessage={getInputProps('typeOfMoney').errorText}
                  isDisabled
                />
              </Grid>
              <Grid item xs={7} sm={6}>
                <MoneyField
                  inputId="sales-web-button-carvalue"
                  inputName="carValue"
                  inputLabel="Valor actual del vehículo"
                  defaultValue={getInputProps('carValue').value as number}
                  inputError={getInputProps('carValue').error}
                  inputErrorMessage={getInputProps('carValue').errorText}
                  onChange={getInputProps('carValue').onChange}
                  currencySelected={
                    getInputProps('typeOfMoney').value as string
                  }
                />
              </Grid>
              {makeHasInvoiceField({
                label: 'Si, tiene factura de compra',
                id: 'sales-web-checkBox-hasInvoice',
                getInputProps,
                key: 'hasInvoice',
                classes,
                isCollectiveProfile,
                onOpenModal: () => setOpenModal(true),
              })}
            </Grid>
          </div>
        </DefaultCard>
      )}
      <PurchaseInvoiceModal
        data-testid="purchase-invoice-modal"
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
    </Grid>
  );
};

export default CarDataSection;
